/**
 * Utility functions for calculating event booking pricing
 */

/**
 * Calculate pricing breakdown for event booking
 * @param {Object} params - Pricing calculation parameters
 * @param {Object} params.event - Event object with basePrice and venue info
 * @param {Object} params.inquiryForm - Form data with booking details
 * @param {boolean} params.isCustomInquiry - Whether this is a custom inquiry
 * @param {Array} params.filteredHotels - Available hotels list
 * @returns {Object} Pricing breakdown object
 */
export const calculatePricingBreakdown = ({
  event,
  inquiryForm,
  isCustomInquiry,
  filteredHotels,
}) => {
  if (!event || !inquiryForm.tripDuration || isCustomInquiry) {
    return {
      baseTotal:
        (event?.basePrice || 0) * Math.max(inquiryForm.numberOfGuests, 1),
      eventDateCost: 0,
      seatingCost: 0,
      hotelCost: 0,
      finalTotal:
        (event?.basePrice || 0) * Math.max(inquiryForm.numberOfGuests, 1),
      pricePerPerson: event?.basePrice || 0,
    };
  }

  const numberOfGuests = Math.max(inquiryForm.numberOfGuests, 1);
  let baseTotal = event.basePrice * numberOfGuests;
  let eventDateCost = 0;
  let seatingCost = 0;
  let hotelCost = 0;

  // Add event date costs
  if (inquiryForm.eventDates && inquiryForm.eventDateCost) {
    eventDateCost = inquiryForm.eventDateCost * numberOfGuests;
  }

  // Add seating preference markup
  if (inquiryForm.seatingPreference) {
    const category = event.venue.seatingPlan?.find(
      (cat) => cat.category === inquiryForm.seatingPreference
    );
    if (category?.markup) {
      seatingCost = category.markup * numberOfGuests;
    }
  }

  // Calculate hotel costs based on selected rooms
  if (inquiryForm.hotelPreference.length > 0) {
    inquiryForm.hotelPreference.forEach((pref) => {
      const hotel = filteredHotels.find((h) => h._id === pref.hotelId);
      const roomType = hotel?.roomTypes.find((rt) => rt.type === pref.roomType);

      if (hotel && roomType) {
        const isDefaultHotel =
          hotel._id.toString() ===
          event.venue?.defaultHotelRoom?.hotelId?.toString();
        const isDefaultRoom =
          roomType.type === event.venue?.defaultHotelRoom?.roomType;

        // Add hotel markup if not default hotel
        if (!isDefaultHotel && hotel.markupPrice) {
          hotelCost += hotel.markupPrice;
        }

        // Add room markup if not default room
        if (!(isDefaultHotel && isDefaultRoom) && roomType.pricePerNight) {
          hotelCost +=
            roomType.pricePerNight;
        }
      }
    });
  }

  const finalTotal = baseTotal + eventDateCost + seatingCost + hotelCost;
  const pricePerPerson = finalTotal / numberOfGuests;

  return {
    baseTotal,
    eventDateCost,
    seatingCost,
    hotelCost,
    finalTotal,
    pricePerPerson,
  };
};

/**
 * Get the appropriate price per person to display based on current step
 * @param {number} step - Current booking step
 * @param {number} hotelPricesPerPersonStep2 - Price per person for steps 1-2
 * @param {number} hotelPricesPerPerson - Price per person for step 3+
 * @returns {string|null} Formatted price string or null
 */
export const getDisplayPrice = (
  hotelLength,
  hotelPricesPerPersonStep2,
  hotelPricesPerPerson
) => {
  const priceToShow =
    hotelLength === 0 ? hotelPricesPerPersonStep2 : hotelPricesPerPerson;
  return priceToShow > 0 ? `€${priceToShow.toLocaleString("en-US", { maximumFractionDigits: 0 })}` : null;
};
